import React, { useRef, useEffect, useState } from "react";
import "./Gallery.css";

export default function Gallery({ images = [] }) {
  const galleryRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [zoomedIdx, setZoomedIdx] = useState(null);

  // refs for animation and measurements
  const originalWidthRef = useRef(0);
  const rafRef = useRef(null);

  // doubled images for seamless loop
  const doubledImages = React.useMemo(() => [...images, ...images], [images]);

  // ensure original width is known (also updated on each image load)
  useEffect(() => {
    const g = galleryRef.current;
    if (g) {
      g.scrollLeft = 0;
      originalWidthRef.current = g.scrollWidth / 2 || 0;
    }
  }, [images]);

  const handleImgLoad = () => {
    const g = galleryRef.current;
    if (g) originalWidthRef.current = g.scrollWidth / 2 || 0;
  };

  // RAF loop: increment scrollLeft and instantly jump back when passing originalWidth
  useEffect(() => {
    let lastTime = null;

    const step = (time) => {
      if (!lastTime) lastTime = time;
      const dt = time - lastTime;
      lastTime = time;

      const g = galleryRef.current;
      if (g && !isHovered && images.length > 1) {
        const speedPxPerSecond = 40; // tweak this for faster/slower scroll
        const delta = (speedPxPerSecond * dt) / 1000;
        g.scrollLeft += delta;

        const originalWidth = originalWidthRef.current || g.scrollWidth / 2;
        if (originalWidth && g.scrollLeft >= originalWidth) {
          // jump back by exactly the original width to create seamless loop
          g.scrollLeft -= originalWidth;
        }
      }

      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      lastTime = null;
    };
  }, [isHovered, images.length]);

  // click handler maps duplicated index back to original index
  const handleImageClick = (idx) => {
    const originalIdx = idx % images.length;
    setZoomedIdx((prev) => (prev === originalIdx ? null : originalIdx));
  };

  return (
    <div
      className="highlight-gallery-container"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="highlight-gallery" ref={galleryRef}>
        {doubledImages.map((img, idx) => {
          const originalIdx = images.length ? idx % images.length : idx;
          return (
            <img
              key={idx}
              src={img}
              alt={`highlight-${originalIdx}`}
              className={`highlight-image ${zoomedIdx === originalIdx ? "zoomed-out" : "zoomed-in"}`}
              onClick={() => handleImageClick(idx)}
              onLoad={handleImgLoad}
              draggable={false}
            />
          );
        })}
      </div>
    </div>
  );
}
